// Outils de répartition de la Grille Commandes (repris de l'ancienne page Répartition) :
// arrêts d'une commande (séparer / recoller, assigner un arrêt seul), routes maîtres, arrêts archivés.
import React, { useEffect, useState } from 'react';
import {
  X, Package, MapPin, Scissors, Link2, UserPlus, UserMinus, Loader2, ArrowUp, ArrowDown, Unlink, Route, Archive, Trash2,
} from 'lucide-react';
import { toast } from 'sonner';

const isPickup = (s) => s.stop_type === 'cueillette' || s.stop_type === 'pickup';

const Shell = ({ testId, title, subtitle, onClose, children, wide }) => (
  <div className="fixed inset-0 z-50 bg-black/70 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose} data-testid={testId}>
    <div className={`bg-slate-900 border border-slate-800 rounded-t-2xl sm:rounded-xl w-full ${wide ? 'sm:max-w-2xl' : 'sm:max-w-lg'} max-h-[85vh] overflow-hidden flex flex-col`} onClick={(e) => e.stopPropagation()}>
      <div className="p-4 border-b border-slate-800 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-white font-bold text-base">{title}</h3>
          {subtitle && <p className="text-xs text-slate-400 mt-0.5">{subtitle}</p>}
        </div>
        <button onClick={onClose} className="p-2 text-slate-400 hover:text-white min-h-11 min-w-11" data-testid={`${testId}-close`}><X size={20} /></button>
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-3">{children}</div>
    </div>
  </div>
);

const StopIcon = ({ stop }) => (
  <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${isPickup(stop) ? 'bg-emerald-500/15' : 'bg-purple-500/15'}`}>
    {isPickup(stop) ? <Package size={14} className="text-emerald-400" /> : <MapPin size={14} className="text-purple-400" />}
  </div>
);

const DriverSelect = ({ drivers, value, onChange, testId }) => (
  <select
    value={value}
    onChange={(e) => onChange(e.target.value)}
    className="bg-slate-800 border border-slate-700 rounded-lg text-xs text-white px-2 py-2 min-h-9 max-w-[180px]"
    data-testid={testId}
  >
    <option value="">Choisir un chauffeur…</option>
    {drivers.map((d) => (
      <option key={d.id} value={d.id}>{`${d.first_name || ''} ${d.last_name || ''}`.trim() || d.email}</option>
    ))}
  </select>
);

// ------------------------------------------------------------
// 2 + 3 : Arrêts de la commande — séparer / recoller, assigner un arrêt seul
// ------------------------------------------------------------
const StopAssignRow = ({ stop, drivers, driverNameById, onAssign, onUnassign }) => {
  const [driverId, setDriverId] = useState('');
  const assignedName = stop.assigned_driver_id ? (driverNameById[stop.assigned_driver_id] || stop.assigned_driver_name || 'Chauffeur') : null;
  return (
    <div className="bg-slate-800/50 border border-slate-800 rounded-lg p-3 flex items-center gap-3 flex-wrap" data-testid={`order-stop-row-${stop.id}`}>
      <StopIcon stop={stop} />
      <div className="flex-1 min-w-[160px]">
        <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-400">{isPickup(stop) ? 'Cueillette' : 'Livraison'}</p>
        <p className="text-white text-sm font-medium truncate">{stop.client_name || stop.address || '—'}</p>
        <p className="text-xs truncate" data-testid={`order-stop-driver-${stop.id}`}>
          {assignedName ? <span className="text-amber-300">{assignedName}</span> : <span className="text-slate-500">Non assigné</span>}
        </p>
      </div>
      <div className="flex items-center gap-2">
        <DriverSelect drivers={drivers} value={driverId} onChange={setDriverId} testId={`order-stop-driver-select-${stop.id}`} />
        <button
          disabled={!driverId}
          onClick={() => onAssign(stop, driverId)}
          className="px-2.5 py-2 rounded-lg bg-amber-500 text-black text-xs font-bold disabled:opacity-40 flex items-center gap-1 min-h-9"
          data-testid={`order-stop-assign-${stop.id}`}
        >
          <UserPlus size={12} /> Assigner
        </button>
        {stop.assigned_driver_id && (
          <button
            onClick={() => onUnassign(stop)}
            className="px-2.5 py-2 rounded-lg bg-slate-700 hover:bg-red-500/20 text-slate-200 hover:text-red-300 text-xs flex items-center gap-1 min-h-9"
            data-testid={`order-stop-unassign-${stop.id}`}
          >
            <UserMinus size={12} /> Retirer
          </button>
        )}
      </div>
    </div>
  );
};

export const OrderStopsModal = ({ order, drivers, driverNameById, api, onClose, onChanged }) => {
  const stops = order.stops || [];
  const isSplit = order.block_status === 'split' || stops.some((s) => s.block_status === 'split');

  const run = async (promise, okMsg) => {
    const res = await promise;
    if (res.success) { toast.success(okMsg); onChanged(); }
    else toast.error(res.error || 'Erreur');
    return res.success;
  };

  const toggleSplit = () => {
    if (!isSplit && !window.confirm("Séparer cette commande ?\n\nLa cueillette et la livraison pourront être assignées à des chauffeurs différents.")) return;
    run(api.post(`/dispatch/orders/${order.id}/${isSplit ? 'relink' : 'split'}`, {}), isSplit ? 'Commande recollée' : 'Commande séparée');
  };

  const assign = (stop, driverId) => run(
    api.post(`/dispatch/stops/${stop.id}/assign`, {
      driver_id: driverId,
      scheduled_time: stop.required_time || null,
      scheduled_date: stop.required_date || null,
    }),
    'Arrêt assigné',
  );
  const unassign = (stop) => run(api.post(`/dispatch/stops/${stop.id}/unassign`, {}), 'Arrêt désassigné');

  return (
    <Shell testId="order-stops-modal" title={`Arrêts — ${order.order_number || ''}`} subtitle="Assigner chaque arrêt séparément ou séparer / recoller la commande" onClose={onClose} wide>
      <div className={`flex items-center justify-between gap-3 rounded-lg p-3 border ${isSplit ? 'border-cyan-500/40 bg-cyan-500/10' : 'border-slate-800 bg-slate-800/40'}`}>
        <p className="text-xs text-slate-300" data-testid="order-split-status">
          {isSplit ? 'Commande séparée : chaque arrêt est indépendant.' : 'Commande liée : cueillette et livraison voyagent ensemble.'}
        </p>
        <button
          onClick={toggleSplit}
          className={`px-3 py-2 rounded-lg text-xs font-bold flex items-center gap-1.5 min-h-9 ${isSplit ? 'bg-cyan-500 text-black' : 'bg-slate-700 text-white hover:bg-slate-600'}`}
          data-testid={isSplit ? 'order-relink-btn' : 'order-split-btn'}
        >
          {isSplit ? <><Link2 size={13} /> Recoller</> : <><Scissors size={13} /> Séparer</>}
        </button>
      </div>
      {stops.map((s) => (
        <StopAssignRow key={s.id} stop={s} drivers={drivers} driverNameById={driverNameById} onAssign={assign} onUnassign={unassign} />
      ))}
    </Shell>
  );
};

// ------------------------------------------------------------
// 1 : Route maître (LIER) — réordonner, retirer, délier, assigner en bloc
// ------------------------------------------------------------
export const RouteBlockModal = ({ blockId, blockName, orders, drivers, api, onClose, onChanged }) => {
  const stops = orders
    .flatMap((o) => (o.stops || []).map((s) => ({ ...s, order_number: o.order_number })))
    .filter((s) => s.route_block_id === blockId)
    .sort((a, b) => (a.route_block_position ?? 0) - (b.route_block_position ?? 0));
  const [driverId, setDriverId] = useState('');
  const [date, setDate] = useState(() => stops[0]?.required_date || new Date().toISOString().slice(0, 10));
  const [time, setTime] = useState('08:00');

  const run = async (promise, okMsg, close = false) => {
    const res = await promise;
    if (res.success) { if (okMsg) toast.success(okMsg); onChanged(); if (close) onClose(); }
    else toast.error(res.error || 'Erreur');
  };

  const move = (idx, dir) => {
    const ids = stops.map((s) => s.id);
    const j = idx + dir;
    if (j < 0 || j >= ids.length) return;
    [ids[idx], ids[j]] = [ids[j], ids[idx]];
    run(api.patch(`/dispatch/route-blocks/${blockId}/reorder`, { stop_ids: ids }));
  };

  return (
    <Shell testId="route-block-modal" title={`Route maître — ${blockName || ''}`} subtitle={`${stops.length} arrêt(s) dans l'ordre de passage`} onClose={onClose} wide>
      {stops.map((s, i) => (
        <div key={s.id} className="bg-slate-800/50 border border-slate-800 rounded-lg p-3 flex items-center gap-3" data-testid={`route-block-stop-${s.id}`}>
          <span className="w-6 text-center text-amber-400 font-bold text-sm">{i + 1}</span>
          <StopIcon stop={s} />
          <div className="flex-1 min-w-0">
            <p className="text-white text-sm font-medium truncate">{s.client_name || s.address}</p>
            <p className="text-slate-500 text-xs truncate">{s.order_number} · {s.city || ''}</p>
          </div>
          <button onClick={() => move(i, -1)} disabled={i === 0} className="p-2 text-slate-400 hover:text-white disabled:opacity-30" data-testid={`route-block-up-${s.id}`}><ArrowUp size={14} /></button>
          <button onClick={() => move(i, 1)} disabled={i === stops.length - 1} className="p-2 text-slate-400 hover:text-white disabled:opacity-30" data-testid={`route-block-down-${s.id}`}><ArrowDown size={14} /></button>
          <button
            onClick={() => run(api.post(`/dispatch/route-blocks/${blockId}/remove-stop/${s.id}`, {}), 'Arrêt retiré de la route', stops.length <= 2)}
            className="p-2 text-slate-400 hover:text-red-400"
            title="Retirer de la route"
            data-testid={`route-block-remove-${s.id}`}
          >
            <X size={14} />
          </button>
        </div>
      ))}
      <div className="border-t border-slate-800 pt-3 flex items-center gap-2 flex-wrap">
        <DriverSelect drivers={drivers} value={driverId} onChange={setDriverId} testId="route-block-driver-select" />
        <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="bg-slate-800 border border-slate-700 rounded-lg text-xs text-white px-2 py-2 min-h-9" data-testid="route-block-date" />
        <input type="time" value={time} onChange={(e) => setTime(e.target.value)} className="bg-slate-800 border border-slate-700 rounded-lg text-xs text-white px-2 py-2 min-h-9" data-testid="route-block-time" />
        <button
          disabled={!driverId}
          onClick={() => run(api.post(`/dispatch/route-blocks/${blockId}/assign`, { driver_id: driverId, scheduled_date: date, scheduled_time: time }), 'Route assignée au chauffeur', true)}
          className="px-3 py-2 rounded-lg bg-amber-500 text-black text-xs font-bold disabled:opacity-40 flex items-center gap-1 min-h-9"
          data-testid="route-block-assign-btn"
        >
          <UserPlus size={12} /> Assigner la route
        </button>
        <button
          onClick={() => window.confirm('Délier complètement cette route maître ?\nLes arrêts redeviennent indépendants.') && run(api.del(`/dispatch/route-blocks/${blockId}`), 'Route déliée', true)}
          className="ml-auto px-3 py-2 rounded-lg bg-slate-700 hover:bg-red-500/20 text-slate-200 hover:text-red-300 text-xs flex items-center gap-1 min-h-9"
          data-testid="route-block-unlink-btn"
        >
          <Unlink size={12} /> Délier
        </button>
      </div>
    </Shell>
  );
};

export const routeBlockStopIds = (orders) => orders.flatMap((o) => {
  const stops = o.stops || [];
  return [...stops.filter(isPickup), ...stops.filter((s) => !isPickup(s))].map((s) => s.id);
});

export const RouteBlockBadge = ({ name, onClick }) => (
  <button
    onClick={(e) => { e.stopPropagation(); onClick(); }}
    className="ml-1.5 inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded bg-cyan-500/15 text-cyan-300 border border-cyan-500/40 hover:bg-cyan-500/25"
    data-testid="route-block-badge"
  >
    <Route size={10} /> {name || 'Route'}
  </button>
);

// ------------------------------------------------------------
// 4 : Arrêts archivés par les chauffeurs
// ------------------------------------------------------------
export const ArchivedStopsModal = ({ api, onClose }) => {
  const [stops, setStops] = useState(null);
  useEffect(() => {
    api.get('/dispatch/admin/archived-stops').then((res) => setStops(res.success ? (res.data.stops || []) : []));
  }, [api]);

  const clear = async (stop) => {
    const res = await api.del(`/dispatch/admin/stops/${stop.id}/permanent`);
    if (res.success) { setStops((prev) => prev.filter((s) => s.id !== stop.id)); toast.success('Arrêt retiré des archives'); }
    else toast.error(res.error || 'Erreur');
  };

  return (
    <Shell testId="archived-stops-modal" title="Arrêts archivés" subtitle="Arrêts archivés par les chauffeurs dans leur application" onClose={onClose} wide>
      {stops === null ? (
        <div className="text-center py-6"><Loader2 className="w-5 h-5 animate-spin text-slate-400 mx-auto" /></div>
      ) : stops.length === 0 ? (
        <p className="text-slate-500 text-sm text-center py-6" data-testid="archived-stops-empty">Aucun arrêt archivé</p>
      ) : stops.map((s) => (
        <div key={s.id} className="flex items-center justify-between bg-slate-800/50 rounded-lg p-3" data-testid={`archived-stop-${s.id}`}>
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <StopIcon stop={s} />
            <div className="min-w-0">
              <p className="text-white text-sm font-medium truncate">{s.client_name}</p>
              <p className="text-slate-500 text-xs truncate">{s.address} · {s.required_date} · {s.assigned_driver_name || 'Non assigné'}</p>
            </div>
          </div>
          <button onClick={() => clear(s)} className="p-2 rounded hover:bg-red-500/20 text-slate-500 hover:text-red-400 shrink-0 ml-2" title="Retirer des archives" data-testid={`archived-stop-clear-${s.id}`}>
            <Trash2 size={14} />
          </button>
        </div>
      ))}
    </Shell>
  );
};

export const ArchivedStopsButton = ({ onClick }) => (
  <button
    onClick={onClick}
    className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs text-slate-300 bg-slate-800 hover:bg-slate-700 min-h-9"
    data-testid="archived-stops-btn"
  >
    <Archive size={13} /> Archivés
  </button>
);
